class ApiService {
  constructor() {
    this.baseURL = process.env.NODE_ENV === 'production' 
      ? 'https://forkast.fly.dev/api' 
      : 'http://localhost:8080/api';
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      // Add timeout for mobile connections
      signal: options.signal || AbortSignal.timeout(30000), // 30 second timeout
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        console.error(`API Error: ${response.status} ${response.statusText} for ${url}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      
      // Provide more specific error messages for mobile debugging
      if (error.name === 'AbortError') {
        console.error('Request timed out - check network connection');
      } else if (error.message.includes('Failed to fetch')) {
        console.error('Network error - check internet connection and server status');
      }
      
      throw error;
    }
  }

  // User methods
  async getUserByEmail(email) {
    return this.request(`/users/by-email/${encodeURIComponent(email)}`);
  }

  async createOrUpdateUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Recipe methods
  async getRecipes() {
    return this.request('/recipes/full');
  }

  async getRecipe(id) {
    return this.request(`/recipes/${id}`);
  }

  async getDetailedRecipe(id) {
    return this.request(`/recipes/${id}/detailed`);
  }

  async getRecipeIngredients(recipeIds) {
    return this.request('/recipes/ingredients', {
      method: 'POST',
      body: JSON.stringify({ recipe_ids: recipeIds }),
    });
  }

  async createRecipe(recipeData) {
    return this.request('/recipes', {
      method: 'POST',
      body: JSON.stringify(recipeData),
    });
  }

  async updateRecipe(id, recipeData) {
    return this.request(`/recipes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(recipeData),
    });
  }

  async deleteRecipe(id) {
    return this.request(`/recipes/${id}`, {
      method: 'DELETE',
    });
  }

  async processRecipeUrl(url) {
    return this.request('/process-recipe-url', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  async processRecipeText(text) {
    return this.request('/process-recipe-text', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  // Rating methods
  async getRatings() {
    return this.request('/ratings');
  }

  async createRating(ratingData) {
    return this.request('/ratings', {
      method: 'POST',
      body: JSON.stringify(ratingData),
    });
  }

  async updateRating(ratingData) {
    return this.request('/ratings', {
      method: 'PUT',
      body: JSON.stringify(ratingData),
    });
  }

  // Favorites methods
  async getFavorites(userId) {
    return this.request(`/users/${userId}/favorites`);
  }

  async addFavorite(userId, recipeId) {
    return this.request(`/users/${userId}/favorites/${recipeId}`, {
      method: 'POST',
      body: JSON.stringify({ action: 'add' }),
    });
  }

  async removeFavorite(userId, recipeId) {
    return this.request(`/users/${userId}/favorites/${recipeId}`, {
      method: 'POST',
      body: JSON.stringify({ action: 'remove' }),
    });
  }

  // Comments methods
  async getComments(recipeId) {
    return this.request(`/comments/${recipeId}`);
  }

  async createComment(commentData) {
    return this.request('/comments', {
      method: 'POST',
      body: JSON.stringify(commentData),
    });
  }

  async deleteComment(commentId) {
    return this.request(`/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  // Meal plan methods
  async getMealPlans(userId) {
    return this.request(`/meal-plans/${userId}`);
  }

  async createMealPlan(mealPlanData) {
    return this.request('/meal-plans', {
      method: 'POST',
      body: JSON.stringify(mealPlanData),
    });
  }

  async deleteMealPlan(mealPlanId) {
    return this.request(`/meal-plans/${mealPlanId}`, {
      method: 'DELETE',
    });
  }

  // Utility methods
  async getStats(userId) {
    return this.request(`/stats/${userId}`);
  }

  async uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request('/upload', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  // Undo-specific methods
  async undoDeleteRecipe(recipeData) {
    return this.request('/undo/restore-recipe', {
      method: 'POST',
      body: JSON.stringify(recipeData),
    });
  }

  async undoUpdateRecipe(id, previousData) {
    return this.request(`/undo/revert-recipe/${id}`, {
      method: 'PUT',
      body: JSON.stringify(previousData),
    });
  }

  async undoCreateRecipe(id) {
    return this.request(`/undo/delete-recipe/${id}`, {
      method: 'DELETE',
    });
  }

  async undoFavorite(userId, recipeId, wasFavorited) {
    if (wasFavorited) {
      return this.addFavorite(userId, recipeId);
    } else {
      return this.removeFavorite(userId, recipeId);
    }
  }

  // Celebration count methods
  async getRecipeCount(userId) {
    return this.request(`/users/${userId}/recipe-count`);
  }

  async getFavoriteCount(userId) {
    return this.request(`/users/${userId}/favorite-count`);
  }

  async getCalendarMealCount(userId) {
    return this.request(`/users/${userId}/calendar-meal-count`);
  }

  // Additional methods to match utils/apiService functionality
  async getUserStats(userId) {
    return this.request(`/users/${userId}/stats`);
  }

  async getUserIdByEmail(email) {
    return this.request(`/users/id-by-email/${encodeURIComponent(email)}`);
  }

  async checkDuplicateRecipe(name) {
    return this.request(`/recipes/check-duplicate/${encodeURIComponent(name)}`);
  }

  async deleteRecipeWithUserId(id, userId) {
    return this.request(`/recipes/${id}?user_id=${userId}`, {
      method: 'DELETE',
    });
  }

  async deleteBrokenRecipeImage(recipeId, imageUrl, userId) {
    return this.request(`/recipes/${recipeId}/images`, {
      method: 'DELETE',
      body: JSON.stringify({ imageUrl, userId }),
    });
  }

  async toggleFavorite(userId, recipeId, action) {
    return this.request(`/users/${userId}/favorites/${recipeId}`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  }

  async getAllRatings() {
    return this.request('/ratings');
  }

  async addOrUpdateRating(userId, recipeId, rating) {
    return this.request('/ratings', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, recipe_id: recipeId, rating }),
    });
  }

  async deleteRating(userId, recipeId) {
    return this.request(`/ratings/${userId}/${recipeId}`, {
      method: 'DELETE',
    });
  }

  async performUndo(undoData) {
    return this.request('/undo', {
      method: 'POST',
      body: JSON.stringify(undoData),
    });
  }

  async addMealPlan(mealPlanData) {
    return this.request('/meal-plans', {
      method: 'POST',
      body: JSON.stringify(mealPlanData),
    });
  }

  async removeMealPlan(id, userId) {
    return this.request(`/meal-plans/${id}?user_id=${userId}`, {
      method: 'DELETE',
    });
  }

  async getRecipesFull() {
    return this.request('/recipes/full');
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService; 